<script setup lang="ts">
import type { Portfolio } from "~/typings/portfolio";
import TimelineExperienceCard from "./TimelineExperienceCard.vue";

interface ExperienceTimelineProps {
  experiences: Portfolio.Experience[];
}

const props = defineProps<ExperienceTimelineProps>();

const emit = defineEmits<{
  selectExperience: [experience: Portfolio.Experience];
}>();
</script>

<template>
  <div class="timeline-container" role="list" aria-label="Experience timeline">
    <!-- Central vertical line -->
    <div class="timeline-line" aria-hidden="true"></div>

    <!-- Timeline items -->
    <template v-if="props.experiences.length">
      <div
        v-for="(experience, index) in props.experiences"
        :key="experience.id"
        class="timeline-row"
      >
        <!-- Dot connector at center -->
        <UiTimelineDot :size="index === 0 ? 'lg' : 'md'" />

        <!-- Placeholder on left for even index -->
        <div v-if="index % 2 !== 0" class="timeline-placeholder timeline-left">
          <!-- Reserved for future: videos, images, links -->
          <!--
          <div class="bg-red-300 h-full flex items-center justify-center">
            <p>Placeholder Left</p>
          </div>
          -->
        </div>

        <!-- Card container with dynamic positioning -->
        <div :class="index % 2 === 0 ? 'timeline-left' : 'timeline-right'">
          <div
            :class="[
              'timeline-connector',
              index % 2 === 0
                ? 'timeline-connector-left'
                : 'timeline-connector-right',
            ]"
            aria-hidden="true"
          ></div>
          <TimelineExperienceCard
            v-bind="experience"
            :index="index"
            role="listitem"
            @click="emit('selectExperience', experience)"
          />
        </div>

        <!-- Placeholder on right for odd index -->
        <div v-if="index % 2 === 0" class="timeline-placeholder timeline-right">
          <!-- Reserved for future: videos, images, links -->
          <!--
          <div class="bg-red-300 h-full flex items-center justify-center">
            <p>Placeholder Right</p>
          </div>
          -->
        </div>
      </div>
    </template>

    <!-- Timeline start marker -->
    <div v-if="props.experiences.length" class="timeline-start-marker">
      <div class="timeline-start-dot" aria-hidden="true"></div>
      <div class="timeline-start-label">
        <UIcon
          name="i-custom-briefcase"
          :size="20"
          mode="svg"
          class="inline-block mr-2"
          aria-hidden="true"
        />
        <span>Start of my professional career</span>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="text-center py-16 text-gray-500 dark:text-gray-400">
      <UIcon
        name="i-custom-briefcase"
        :size="64"
        mode="svg"
        class="mx-auto mb-4 text-gray-400 dark:text-gray-600"
        aria-hidden="true"
      />
      <p class="text-xl font-medium">No experience to display yet.</p>
      <p class="text-sm mt-2">Check back soon for updates.</p>
    </div>
  </div>
</template>

<style scoped>
.timeline-container {
  position: relative;
  max-width: 80rem;
  margin-left: auto;
  margin-right: auto;
}

/* Central vertical line */
.timeline-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  transform: translateX(-50%);
  background-image: linear-gradient(
    to bottom,
    var(--color-secondary-400),
    var(--color-secondary-500),
    var(--color-secondary-600)
  );
}

/* Timeline row with alternating sides */
.timeline-row {
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
  min-height: 120px;
}

@media (min-width: 768px) {
  .timeline-row {
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
  }
}

@media (min-width: 1024px) {
  .timeline-row {
    min-height: 140px;
    margin-bottom: 2.5rem;
  }
}

/* Left side card container */
.timeline-left {
  grid-column: 1;
  position: relative;
  display: flex;
  align-items: flex-start;
}

@media (min-width: 768px) {
  .timeline-left {
    padding-right: 2rem;
  }
}

@media (min-width: 1024px) {
  .timeline-left {
    /* Maintain space when card goes absolute */
    min-height: 140px;
  }
}

/* Right side card container */
.timeline-right {
  grid-column: 1;
  position: relative;
  display: flex;
  align-items: flex-start;
}

@media (min-width: 768px) {
  .timeline-right {
    grid-column-start: 2;
    padding-left: 2rem;
  }
}

@media (min-width: 1024px) {
  .timeline-right {
    /* Maintain space when card goes absolute */
    min-height: 140px;
  }
}

/* Placeholder for empty side - hidden to reduce vertical space */
.timeline-placeholder {
  display: none;
}

/* Connecting line from dot to card */
.timeline-connector {
  position: absolute;
  top: 2.5rem;
  height: 2px;
  background-color: var(--color-secondary-300);
  display: none;
  transition: all 0.3s;
}

@media (min-width: 768px) {
  .timeline-connector {
    display: block;
  }
}

.dark .timeline-connector {
  background-color: var(--color-secondary-700);
}

.timeline-row:hover .timeline-connector {
  background-color: var(--color-secondary-500);
}

/* Left connector */
.timeline-connector-left {
  right: 50%;
  margin-right: 0.5rem;
  width: calc(50% - 2rem);
}

/* Right connector */
.timeline-connector-right {
  left: 50%;
  margin-left: 0.5rem;
  width: calc(50% - 2rem);
}

/* Timeline start marker */
.timeline-start-marker {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 1rem;
  padding-bottom: 2rem;
}

.timeline-start-dot {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background-color: var(--color-secondary-500);
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1);
  box-shadow: 0 0 0 4px white;
  z-index: 10;
  transition: all 0.3s;
}

.dark .timeline-start-dot {
  box-shadow: 0 0 0 4px var(--color-primary-950);
}

.timeline-start-label {
  margin-top: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  background-color: var(--color-secondary-50);
  color: var(--color-secondary-700);
  font-size: 0.875rem;
  font-weight: 600;
  text-align: center;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
  transition: all 0.3s;
}

.dark .timeline-start-label {
  background-color: var(--color-secondary-700);
  color: var(--color-secondary-100);
}
</style>
